/**
 * Processes Page — Process Template Library & Builder.
 *
 * Replaces the legacy FormTemplatesPage with a full enterprise-grade
 * process template builder. Two views:
 *
 *   1. Process Library — lists all templates with filtering/search.
 *   2. Process Builder — three-panel layout (palette, steps, properties)
 *      for composing step-based process templates.
 *
 * API: /api/v1/processes/templates (CRUD), /api/v1/processes/categories
 * Design: Apex Design System tokens, lucide-react icons.
 */

import { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import ProcessPreview from "@/features/processes/ProcessPreview";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Card from "@/components/ui/Card";
import Input from "@/components/ui/Input";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import {
  ArrowLeft,
  Plus,
  Search,
  FileText,
  Trash2,
  GripVertical,
  Pencil,
  Send,
  CheckSquare,
  Type,
  Hash,
  DollarSign,
  Calendar,
  List,
  ListChecks,
  ThumbsUp,
  ClipboardCheck,
  Camera,
  PenTool,
  Gauge,
  Heading,
  Info,
  ChevronDown,
  ChevronRight,
  Settings2,
  X,
  Eye,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProcessStep {
  id: string;
  template_id: string;
  sort_order: number;
  step_type: string;
  label: string;
  description: string | null;
  is_required: boolean;
  section: string | null;
  config: Record<string, unknown> | null;
  score_points: number;
  conditional_on: Record<string, unknown> | null;
  created_at: string;
}

interface ProcessTemplate {
  id: string;
  organization_id: string;
  name: string;
  description: string | null;
  category: string | null;
  icon: string | null;
  entity_type: string;
  version: number;
  status: string;
  scoring_enabled: boolean;
  scoring_goal: number | null;
  estimated_minutes: number | null;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  steps: ProcessStep[];
}

/** Local step representation used while building (before save). */
interface BuilderStep {
  _clientId: string;
  step_type: string;
  label: string;
  description: string;
  is_required: boolean;
  section: string;
  config: Record<string, unknown>;
  score_points: number;
  conditional_on: { step_id: string; operator: string; value: string } | null;
}

// ---------------------------------------------------------------------------
// Step type catalogue — grouped for the palette
// ---------------------------------------------------------------------------

interface StepTypeDef {
  type: string;
  label: string;
  icon: React.ReactNode;
}

const STEP_GROUPS: { group: string; items: StepTypeDef[] }[] = [
  {
    group: "Inputs",
    items: [
      {
        type: "checkbox",
        label: "Checkbox",
        icon: <CheckSquare className="h-4 w-4" />,
      },
      { type: "text", label: "Text", icon: <Type className="h-4 w-4" /> },
      { type: "number", label: "Number", icon: <Hash className="h-4 w-4" /> },
      {
        type: "amount",
        label: "Amount",
        icon: <DollarSign className="h-4 w-4" />,
      },
      { type: "date", label: "Date", icon: <Calendar className="h-4 w-4" /> },
    ],
  },
  {
    group: "Selection",
    items: [
      { type: "select", label: "Select", icon: <List className="h-4 w-4" /> },
      {
        type: "multiselect",
        label: "Multi-select",
        icon: <ListChecks className="h-4 w-4" />,
      },
      {
        type: "yes_no_na",
        label: "Yes / No / N/A",
        icon: <ThumbsUp className="h-4 w-4" />,
      },
      {
        type: "inspection",
        label: "Inspection",
        icon: <ClipboardCheck className="h-4 w-4" />,
      },
      {
        type: "checklist",
        label: "Checklist",
        icon: <ListChecks className="h-4 w-4" />,
      },
    ],
  },
  {
    group: "Media",
    items: [
      { type: "photo", label: "Photo", icon: <Camera className="h-4 w-4" /> },
      {
        type: "signature",
        label: "Signature",
        icon: <PenTool className="h-4 w-4" />,
      },
    ],
  },
  {
    group: "Readings",
    items: [
      {
        type: "meter_reading",
        label: "Meter Reading",
        icon: <Gauge className="h-4 w-4" />,
      },
    ],
  },
  {
    group: "Layout",
    items: [
      {
        type: "heading",
        label: "Heading",
        icon: <Heading className="h-4 w-4" />,
      },
      {
        type: "instruction",
        label: "Instruction",
        icon: <Info className="h-4 w-4" />,
      },
    ],
  },
];

/** Flatten for quick lookups. */
const ALL_STEP_TYPES: StepTypeDef[] = STEP_GROUPS.flatMap((g) => g.items);

function stepIcon(stepType: string) {
  return (
    ALL_STEP_TYPES.find((s) => s.type === stepType)?.icon ?? (
      <FileText className="h-4 w-4" />
    )
  );
}

// ---------------------------------------------------------------------------
// Unique ID helper (client-side only)
// ---------------------------------------------------------------------------

let _cid = 0;
function cid(): string {
  return `_step_${++_cid}_${Date.now()}`;
}

// ---------------------------------------------------------------------------
// API hooks
// ---------------------------------------------------------------------------

/** API returns list response (no steps array — uses step_count instead). */
interface ProcessTemplateSummary extends Omit<ProcessTemplate, "steps"> {
  step_count: number;
}

function useTemplates() {
  return useQuery<ProcessTemplateSummary[]>({
    queryKey: ["process-templates"],
    queryFn: async () => {
      const { data } = await apiClient.get("/api/v1/processes/templates");
      return data;
    },
  });
}

function useCategories() {
  return useQuery<string[]>({
    queryKey: ["process-categories"],
    queryFn: async () => {
      const { data } = await apiClient.get("/api/v1/processes/categories");
      return data?.categories ?? [];
    },
  });
}

function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id?: string;
      body: Record<string, unknown>;
    }) => {
      if (payload.id) {
        const { data } = await apiClient.put(
          `/api/v1/processes/templates/${payload.id}`,
          payload.body,
        );
        return data;
      }
      const { data } = await apiClient.post(
        "/api/v1/processes/templates",
        payload.body,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["process-templates"] }),
  });
}

function usePublishTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apiClient.post(
        `/api/v1/processes/templates/${id}/publish`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["process-templates"] }),
  });
}

function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/processes/templates/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["process-templates"] }),
  });
}

// ---------------------------------------------------------------------------
// Sub-component: StepPalette (left sidebar)
// ---------------------------------------------------------------------------

function StepPalette({ onAdd }: { onAdd: (type: string) => void }) {
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  const toggle = (group: string) =>
    setCollapsed((prev) => ({ ...prev, [group]: !prev[group] }));

  return (
    <aside
      className="w-56 shrink-0 overflow-y-auto border-r border-[var(--border-default)] bg-[var(--surface-sunken)]"
      style={{ height: "calc(100vh - 160px)" }}
    >
      <div className="px-3 py-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Step Types
        </p>
      </div>
      {STEP_GROUPS.map((g) => (
        <div key={g.group}>
          <button
            type="button"
            onClick={() => toggle(g.group)}
            className="flex w-full items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-[var(--text-secondary)] hover:bg-[var(--surface-base)]"
          >
            {collapsed[g.group] ? (
              <ChevronRight className="h-3 w-3" />
            ) : (
              <ChevronDown className="h-3 w-3" />
            )}
            {g.group}
          </button>
          {!collapsed[g.group] && (
            <div className="space-y-0.5 px-2 pb-2">
              {g.items.map((item) => (
                <button
                  key={item.type}
                  type="button"
                  onClick={() => onAdd(item.type)}
                  className="flex w-full items-center gap-2 rounded-[var(--radius-md)] px-2 py-1.5 text-sm text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors duration-150"
                >
                  <span className="text-[var(--text-tertiary)]">
                    {item.icon}
                  </span>
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: StepCard (in main list)
// ---------------------------------------------------------------------------

function StepCard({
  step,
  index,
  isSelected,
  onSelect,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
}: {
  step: BuilderStep;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
}) {
  return (
    <div
      onClick={onSelect}
      className={`group flex items-center gap-3 rounded-[var(--radius-lg)] border px-3 py-2.5 cursor-pointer transition-all duration-150 ${
        isSelected
          ? "border-[var(--color-interactive-primary)] bg-[var(--color-interactive-primary)]/[0.06] shadow-sm"
          : "border-[var(--border-default)] bg-[var(--surface-base)] hover:border-[var(--border-strong)]"
      }`}
    >
      {/* Drag / reorder handle */}
      <div className="flex flex-col gap-0.5 shrink-0">
        <button
          type="button"
          disabled={isFirst}
          onClick={(e) => {
            e.stopPropagation();
            onMoveUp();
          }}
          className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-30 disabled:pointer-events-none text-xs leading-none"
          title="Move up"
        >
          ▲
        </button>
        <GripVertical className="h-4 w-4 text-[var(--text-tertiary)]" />
        <button
          type="button"
          disabled={isLast}
          onClick={(e) => {
            e.stopPropagation();
            onMoveDown();
          }}
          className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-30 disabled:pointer-events-none text-xs leading-none"
          title="Move down"
        >
          ▼
        </button>
      </div>

      {/* Icon + content */}
      <span className="text-[var(--text-secondary)]">
        {stepIcon(step.step_type)}
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-[var(--text-primary)] truncate">
            {step.label || `Step ${index + 1}`}
          </span>
          {step.is_required && <Badge variant="danger">required</Badge>}
          {step.section && <Badge>{step.section}</Badge>}
        </div>
        <span className="text-[11px] text-[var(--text-tertiary)]">
          {ALL_STEP_TYPES.find((s) => s.type === step.step_type)?.label ??
            step.step_type}
        </span>
      </div>

      {/* Delete */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        className="shrink-0 text-[var(--text-tertiary)] hover:text-[var(--text-danger)] opacity-0 group-hover:opacity-100 transition-opacity duration-150"
        title="Remove step"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: StepPropertiesPanel (right sidebar)
// ---------------------------------------------------------------------------

function StepPropertiesPanel({
  step,
  allSteps,
  scoringEnabled,
  onChange,
}: {
  step: BuilderStep;
  allSteps: BuilderStep[];
  scoringEnabled: boolean;
  onChange: (patch: Partial<BuilderStep>) => void;
}) {
  const typeDef = ALL_STEP_TYPES.find((s) => s.type === step.step_type);
  const needsOptions = ["select", "multiselect", "checklist"].includes(
    step.step_type,
  );
  const needsMinMax = ["number", "meter_reading"].includes(step.step_type);
  const isAmount = step.step_type === "amount";
  const isInspection = step.step_type === "inspection";
  const isInstruction = step.step_type === "instruction";

  /** Helper: update nested config value. */
  const setConfig = (key: string, value: unknown) => {
    onChange({ config: { ...step.config, [key]: value } });
  };

  /** Manage option items for select/multiselect/checklist. */
  const options: string[] = (step.config.options as string[]) ?? [];
  const [newOption, setNewOption] = useState("");
  const addOption = () => {
    if (!newOption.trim()) return;
    setConfig("options", [...options, newOption.trim()]);
    setNewOption("");
  };
  const removeOption = (idx: number) => {
    setConfig(
      "options",
      options.filter((_, i) => i !== idx),
    );
  };

  return (
    <aside
      className="w-72 shrink-0 overflow-y-auto border-l border-[var(--border-default)] bg-[var(--surface-sunken)]"
      style={{ height: "calc(100vh - 160px)" }}
    >
      <div className="px-4 py-3 border-b border-[var(--border-default)]">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Step Properties
        </p>
      </div>

      <div className="space-y-4 px-4 py-4">
        {/* Type (read-only) */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-[var(--text-primary)]">
            Type
          </label>
          <div className="flex items-center gap-2 rounded-[var(--radius-md)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-secondary)] border border-[var(--border-default)]">
            {typeDef?.icon}
            <span>{typeDef?.label ?? step.step_type}</span>
          </div>
        </div>

        {/* Label */}
        <Input
          label="Label"
          value={step.label}
          onChange={(e) => onChange({ label: e.target.value })}
          placeholder="e.g., Engine hours"
        />

        {/* Description */}
        <div className="space-y-1.5">
          <label className="block text-sm font-medium text-[var(--text-primary)]">
            Description
          </label>
          <textarea
            value={step.description}
            onChange={(e) => onChange({ description: e.target.value })}
            rows={2}
            placeholder="Optional helper text"
            className="block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          />
        </div>

        {/* Required */}
        <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
          <input
            type="checkbox"
            checked={step.is_required}
            onChange={(e) => onChange({ is_required: e.target.checked })}
            className="h-4 w-4 rounded border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
          />
          Required
        </label>

        {/* Section */}
        <Input
          label="Section"
          value={step.section}
          onChange={(e) => onChange({ section: e.target.value })}
          placeholder="e.g., General Info"
        />

        {/* --- Type-specific config --- */}

        {needsOptions && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Options
            </label>
            {options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <span className="flex-1 truncate rounded-[var(--radius-md)] bg-[var(--surface-base)] border border-[var(--border-default)] px-2 py-1 text-sm text-[var(--text-primary)]">
                  {opt}
                </span>
                <button
                  type="button"
                  onClick={() => removeOption(idx)}
                  className="text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
            <div className="flex gap-2">
              <input
                value={newOption}
                onChange={(e) => setNewOption(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addOption();
                  }
                }}
                placeholder="Add option..."
                className="flex-1 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={addOption}
              >
                Add
              </Button>
            </div>
          </div>
        )}

        {needsMinMax && (
          <>
            <Input
              label="Min"
              type="number"
              value={String(step.config.min ?? "")}
              onChange={(e) =>
                setConfig(
                  "min",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
            />
            <Input
              label="Max"
              type="number"
              value={String(step.config.max ?? "")}
              onChange={(e) =>
                setConfig(
                  "max",
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
            />
            <Input
              label="Unit"
              value={String(step.config.unit ?? "")}
              onChange={(e) => setConfig("unit", e.target.value)}
              placeholder="e.g., hours, km"
            />
          </>
        )}

        {isAmount && (
          <Input
            label="Currency"
            value={String(step.config.currency ?? "")}
            onChange={(e) => setConfig("currency", e.target.value)}
            placeholder="e.g., USD"
          />
        )}

        {isInspection && (
          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer">
            <input
              type="checkbox"
              checked={Boolean(step.config.fail_triggers_corrective)}
              onChange={(e) =>
                setConfig("fail_triggers_corrective", e.target.checked)
              }
              className="h-4 w-4 rounded border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
            />
            Fail triggers corrective action
          </label>
        )}

        {isInstruction && (
          <>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-[var(--text-primary)]">
                Content
              </label>
              <textarea
                value={String(step.config.content ?? "")}
                onChange={(e) => setConfig("content", e.target.value)}
                rows={3}
                placeholder="Instruction text shown to the user"
                className="block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            </div>
            <Input
              label="Image URL"
              value={String(step.config.image_url ?? "")}
              onChange={(e) => setConfig("image_url", e.target.value)}
              placeholder="https://..."
            />
          </>
        )}

        {/* Score points */}
        {scoringEnabled && (
          <Input
            label="Score Points"
            type="number"
            value={String(step.score_points)}
            onChange={(e) =>
              onChange({ score_points: Number(e.target.value) || 0 })
            }
          />
        )}

        {/* Conditional logic */}
        <div className="space-y-1.5 pt-2 border-t border-[var(--border-default)]">
          <label className="block text-sm font-medium text-[var(--text-primary)]">
            Conditional Logic
          </label>
          <p className="text-[11px] text-[var(--text-tertiary)]">
            Show this step only when another step meets a condition.
          </p>

          <select
            value={step.conditional_on?.step_id ?? ""}
            onChange={(e) => {
              if (!e.target.value) {
                onChange({ conditional_on: null });
              } else {
                onChange({
                  conditional_on: {
                    step_id: e.target.value,
                    operator: step.conditional_on?.operator ?? "equals",
                    value: step.conditional_on?.value ?? "",
                  },
                });
              }
            }}
            className="block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          >
            <option value="">None</option>
            {allSteps
              .filter((s) => s._clientId !== step._clientId)
              .map((s, idx) => (
                <option key={s._clientId} value={s._clientId}>
                  {s.label || `Step ${idx + 1}`}
                </option>
              ))}
          </select>

          {step.conditional_on && (
            <div className="grid grid-cols-2 gap-2">
              <select
                value={step.conditional_on.operator}
                onChange={(e) =>
                  onChange({
                    conditional_on: {
                      ...step.conditional_on!,
                      operator: e.target.value,
                    },
                  })
                }
                className="rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              >
                <option value="equals">Equals</option>
                <option value="not_equals">Not equals</option>
              </select>
              <input
                value={step.conditional_on.value}
                onChange={(e) =>
                  onChange({
                    conditional_on: {
                      ...step.conditional_on!,
                      value: e.target.value,
                    },
                  })
                }
                placeholder="Value"
                className="rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-2 py-1.5 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
              />
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: TemplateSettings (collapsible panel)
// ---------------------------------------------------------------------------

interface TemplateSettings {
  name: string;
  description: string;
  category: string;
  icon: string;
  entity_type: string;
  scoring_enabled: boolean;
  scoring_goal: number | null;
  estimated_minutes: number | null;
}

function TemplateSettingsPanel({
  settings,
  categories,
  onChange,
}: {
  settings: TemplateSettings;
  categories: string[];
  onChange: (patch: Partial<TemplateSettings>) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <Card padding="none" className="mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium text-[var(--text-primary)] hover:bg-[var(--surface-base)] transition-colors duration-150"
      >
        <span className="flex items-center gap-2">
          <Settings2 className="h-4 w-4 text-[var(--text-tertiary)]" />
          Template Settings
        </span>
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </button>

      {open && (
        <div className="grid grid-cols-1 gap-4 border-t border-[var(--border-default)] px-4 py-4 sm:grid-cols-2 lg:grid-cols-3">
          <Input
            label="Name"
            value={settings.name}
            onChange={(e) => onChange({ name: e.target.value })}
            placeholder="Process name"
          />
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Description
            </label>
            <textarea
              value={settings.description}
              onChange={(e) => onChange({ description: e.target.value })}
              rows={2}
              placeholder="What is this process for?"
              className="block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Category
            </label>
            <select
              value={settings.category}
              onChange={(e) => onChange({ category: e.target.value })}
              className="block w-full h-9 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            >
              <option value="">No category</option>
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Icon"
            value={settings.icon}
            onChange={(e) => onChange({ icon: e.target.value })}
            placeholder="Emoji or short text"
          />
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-[var(--text-primary)]">
              Entity Type
            </label>
            <select
              value={settings.entity_type}
              onChange={(e) => onChange({ entity_type: e.target.value })}
              className="block w-full h-9 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
            >
              <option value="any">Any</option>
              <option value="work_order">Work Order</option>
              <option value="asset">Asset</option>
            </select>
          </div>
          <Input
            label="Estimated Minutes"
            type="number"
            value={
              settings.estimated_minutes != null
                ? String(settings.estimated_minutes)
                : ""
            }
            onChange={(e) =>
              onChange({
                estimated_minutes:
                  e.target.value === "" ? null : Number(e.target.value),
              })
            }
            placeholder="e.g., 30"
          />
          <label className="flex items-center gap-2 text-sm text-[var(--text-primary)] cursor-pointer self-end pb-1">
            <input
              type="checkbox"
              checked={settings.scoring_enabled}
              onChange={(e) => onChange({ scoring_enabled: e.target.checked })}
              className="h-4 w-4 rounded border-[var(--border-default)] accent-[var(--color-interactive-primary)]"
            />
            Scoring enabled
          </label>
          {settings.scoring_enabled && (
            <Input
              label="Scoring Goal"
              type="number"
              value={
                settings.scoring_goal != null
                  ? String(settings.scoring_goal)
                  : ""
              }
              onChange={(e) =>
                onChange({
                  scoring_goal:
                    e.target.value === "" ? null : Number(e.target.value),
                })
              }
              placeholder="e.g., 100"
            />
          )}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ProcessBuilder
// ---------------------------------------------------------------------------

function ProcessBuilder({
  template,
  categories,
  onClose,
}: {
  template: ProcessTemplateSummary | null;
  categories: string[];
  onClose: () => void;
}) {
  const saveMutation = useSaveTemplate();
  const publishMutation = usePublishTemplate();

  // Fetch full template with steps when editing.
  const { data: fullTemplate } = useQuery<ProcessTemplate>({
    queryKey: ["process-template-detail", template?.id],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/processes/templates/${template!.id}`)).data,
    enabled: !!template?.id,
  });

  // --- Template settings state ---
  const [settings, setSettings] = useState<TemplateSettings>(() => ({
    name: template?.name ?? "",
    description: template?.description ?? "",
    category: template?.category ?? "",
    icon: template?.icon ?? "",
    entity_type: template?.entity_type ?? "any",
    scoring_enabled: template?.scoring_enabled ?? false,
    scoring_goal: template?.scoring_goal ?? null,
    estimated_minutes: template?.estimated_minutes ?? null,
  }));

  // --- Steps state (populated from fullTemplate when editing) ---
  const [steps, setSteps] = useState<BuilderStep[]>([]);
  const [stepsInitialized, setStepsInitialized] = useState(!template?.id);

  // Load steps from full template once fetched.
  if (fullTemplate && !stepsInitialized) {
    setStepsInitialized(true);
    const loaded = (fullTemplate.steps ?? [])
      .slice()
      .sort((a: ProcessStep, b: ProcessStep) => a.sort_order - b.sort_order)
      .map((s: ProcessStep) => ({
        _clientId: s.id || cid(),
        step_type: s.step_type,
        label: s.label,
        description: s.description ?? "",
        is_required: s.is_required,
        section: s.section ?? "",
        config: (s.config ?? {}) as Record<string, unknown>,
        score_points: s.score_points,
        conditional_on: s.conditional_on as BuilderStep["conditional_on"],
      }));
    setSteps(loaded);
  }

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const selectedStep = steps.find((s) => s._clientId === selectedId) ?? null;
  const [showPreview, setShowPreview] = useState(false);

  // --- Step manipulation callbacks ---
  const addStep = useCallback((type: string) => {
    const id = cid();
    const typeDef = ALL_STEP_TYPES.find((s) => s.type === type);
    setSteps((prev) => [
      ...prev,
      {
        _clientId: id,
        step_type: type,
        label: typeDef?.label ?? type,
        description: "",
        is_required: false,
        section: "",
        config: {},
        score_points: 0,
        conditional_on: null,
      },
    ]);
    setSelectedId(id);
  }, []);

  const deleteStep = useCallback((clientId: string) => {
    setSteps((prev) => prev.filter((s) => s._clientId !== clientId));
    setSelectedId((prev) => (prev === clientId ? null : prev));
  }, []);

  const moveStep = useCallback((clientId: string, direction: -1 | 1) => {
    setSteps((prev) => {
      const idx = prev.findIndex((s) => s._clientId === clientId);
      if (idx < 0) return prev;
      const target = idx + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const a = next[idx]!;
      const b = next[target]!;
      next[idx] = b;
      next[target] = a;
      return next;
    });
  }, []);

  const patchStep = useCallback(
    (clientId: string, patch: Partial<BuilderStep>) => {
      setSteps((prev) =>
        prev.map((s) => (s._clientId === clientId ? { ...s, ...patch } : s)),
      );
    },
    [],
  );

  // --- Assemble payload ---
  const buildPayload = () => ({
    name: settings.name,
    description: settings.description || null,
    category: settings.category || null,
    icon: settings.icon || null,
    entity_type: settings.entity_type,
    scoring_enabled: settings.scoring_enabled,
    scoring_goal: settings.scoring_enabled ? settings.scoring_goal : null,
    estimated_minutes: settings.estimated_minutes,
    steps: steps.map((s, idx) => ({
      step_type: s.step_type,
      label: s.label,
      description: s.description || null,
      is_required: s.is_required,
      section: s.section || null,
      config: Object.keys(s.config).length > 0 ? s.config : null,
      score_points: s.score_points,
      sort_order: idx,
      conditional_on: s.conditional_on ?? null,
    })),
  });

  const handleSaveDraft = async () => {
    if (!settings.name.trim()) {
      toast.error("Validation error", "Process name is required.");
      return;
    }
    try {
      await saveMutation.mutateAsync({
        id: template?.id,
        body: buildPayload(),
      });
      toast.success("Draft saved");
      onClose();
    } catch {
      toast.error("Save failed", "Could not save the process template.");
    }
  };

  const handlePublish = async () => {
    if (!settings.name.trim()) {
      toast.error("Validation error", "Process name is required.");
      return;
    }
    if (steps.length === 0) {
      toast.error(
        "Validation error",
        "Add at least one step before publishing.",
      );
      return;
    }
    try {
      // Save first, then publish.
      const saved = await saveMutation.mutateAsync({
        id: template?.id,
        body: buildPayload(),
      });
      await publishMutation.mutateAsync(saved.id ?? template?.id);
      toast.success("Process published");
      onClose();
    } catch {
      toast.error("Publish failed", "Could not publish the process template.");
    }
  };

  return (
    <div className="space-y-0">
      {/* Builder header */}
      <div className="flex items-center justify-between gap-4 pb-4">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={onClose}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <input
            value={settings.name}
            onChange={(e) =>
              setSettings((p) => ({ ...p, name: e.target.value }))
            }
            placeholder="Untitled process"
            className="text-xl font-bold text-[var(--text-primary)] bg-transparent border-none outline-none placeholder:text-[var(--text-tertiary)] min-w-0 flex-1"
          />
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowPreview(true)}
            disabled={steps.length === 0}
          >
            <Eye className="h-3.5 w-3.5" />
            Preview
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleSaveDraft}
            isLoading={saveMutation.isPending}
          >
            Save Draft
          </Button>
          <Button
            size="sm"
            onClick={handlePublish}
            isLoading={publishMutation.isPending}
          >
            <Send className="h-3.5 w-3.5" />
            Publish
          </Button>
        </div>
      </div>

      {/* Settings */}
      <TemplateSettingsPanel
        settings={settings}
        categories={categories}
        onChange={(patch) => setSettings((p) => ({ ...p, ...patch }))}
      />

      {/* Three-panel builder layout */}
      <div
        className="flex rounded-[var(--radius-lg)] border border-[var(--border-default)] overflow-hidden bg-[var(--surface-base)]"
        style={{ height: "calc(100vh - 320px)", minHeight: 400 }}
      >
        {/* Left: palette */}
        <StepPalette onAdd={addStep} />

        {/* Center: step list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {steps.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <FileText className="h-10 w-10 text-[var(--text-tertiary)] mb-3" />
              <p className="text-sm font-medium text-[var(--text-primary)]">
                No steps yet
              </p>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Click a step type in the left panel to start building.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {steps.map((step, idx) => (
                <StepCard
                  key={step._clientId}
                  step={step}
                  index={idx}
                  isSelected={step._clientId === selectedId}
                  onSelect={() => setSelectedId(step._clientId)}
                  onDelete={() => deleteStep(step._clientId)}
                  onMoveUp={() => moveStep(step._clientId, -1)}
                  onMoveDown={() => moveStep(step._clientId, 1)}
                  isFirst={idx === 0}
                  isLast={idx === steps.length - 1}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right: properties */}
        {selectedStep ? (
          <StepPropertiesPanel
            key={selectedStep._clientId}
            step={selectedStep}
            allSteps={steps}
            scoringEnabled={settings.scoring_enabled}
            onChange={(patch) => patchStep(selectedStep._clientId, patch)}
          />
        ) : (
          <aside
            className="w-72 shrink-0 flex items-center justify-center border-l border-[var(--border-default)] bg-[var(--surface-sunken)]"
            style={{ height: "calc(100vh - 320px)" }}
          >
            <p className="text-sm text-[var(--text-tertiary)] text-center px-6">
              Select a step to edit its properties.
            </p>
          </aside>
        )}
      </div>

      {/* Preview modal */}
      {showPreview && (
        <ProcessPreview
          name={settings.name}
          description={settings.description}
          category={settings.category}
          icon={settings.icon}
          estimatedMinutes={settings.estimated_minutes}
          scoringEnabled={settings.scoring_enabled}
          steps={steps.map((s) => ({
            step_type: s.step_type,
            label: s.label,
            description: s.description || null,
            is_required: s.is_required,
            section: s.section || null,
            config: Object.keys(s.config).length > 0 ? s.config : null,
          }))}
          onClose={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-component: ProcessLibrary
// ---------------------------------------------------------------------------

function ProcessLibrary({
  onNew,
  onEdit,
}: {
  onNew: () => void;
  onEdit: (tpl: ProcessTemplateSummary) => void;
}) {
  const { data: templates, isLoading } = useTemplates();
  const { data: categories } = useCategories();
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Fetch full template for preview.
  const { data: previewTemplate } = useQuery<ProcessTemplate>({
    queryKey: ["process-template-detail", previewId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/processes/templates/${previewId}`)).data,
    enabled: !!previewId,
  });
  const publishMutation = usePublishTemplate();
  const deleteMutation = useDeleteTemplate();

  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "draft" | "published"
  >("all");

  const filtered = useMemo(() => {
    if (!templates) return [];
    return templates.filter((t) => {
      if (search && !t.name.toLowerCase().includes(search.toLowerCase()))
        return false;
      if (filterCategory && t.category !== filterCategory) return false;
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      return true;
    });
  }, [templates, search, filterCategory, filterStatus]);

  const handlePublish = async (id: string) => {
    try {
      await publishMutation.mutateAsync(id);
      toast.success("Process published");
    } catch {
      toast.error("Publish failed");
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success("Process deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Processes
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Build and manage step-based process templates for inspections,
            checklists, and workflows.
          </p>
        </div>
        <Button onClick={onNew}>
          <Plus className="h-4 w-4" />
          New Process
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search processes..."
            className="block w-full h-9 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] pl-9 pr-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="h-9 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
        >
          <option value="">All categories</option>
          {(categories ?? []).map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) =>
            setFilterStatus(e.target.value as "all" | "draft" | "published")
          }
          className="h-9 rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-[var(--focus-ring)]"
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="published">Published</option>
        </select>
      </div>

      {/* Template list */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((t) => (
            <Card key={t.id} padding="none">
              <div className="flex items-center gap-4 px-5 py-4">
                {/* Icon */}
                <span className="flex h-10 w-10 items-center justify-center rounded-[var(--radius-md)] bg-[var(--surface-sunken)] text-lg shrink-0">
                  {t.icon || (
                    <FileText className="h-5 w-5 text-[var(--text-tertiary)]" />
                  )}
                </span>

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-[var(--text-primary)] truncate">
                      {t.name}
                    </span>
                    {t.category && <Badge>{t.category}</Badge>}
                    <Badge
                      variant={t.status === "published" ? "success" : "warning"}
                    >
                      {t.status}
                    </Badge>
                    <span className="text-[11px] text-[var(--text-tertiary)]">
                      v{t.version}
                    </span>
                  </div>
                  {t.description && (
                    <p className="mt-0.5 text-xs text-[var(--text-secondary)] truncate max-w-lg">
                      {t.description}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-[11px] text-[var(--text-tertiary)]">
                    <span>{t.step_count ?? 0} steps</span>
                    {t.estimated_minutes != null && (
                      <span>~{t.estimated_minutes} min</span>
                    )}
                    <span>{t.entity_type}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewId(t.id)}
                    title="Preview"
                  >
                    <Eye className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEdit(t)}
                    title="Edit"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {t.status === "draft" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handlePublish(t.id)}
                      isLoading={publishMutation.isPending}
                      title="Publish"
                    >
                      <Send className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(t.id)}
                    isLoading={deleteMutation.isPending}
                    title="Delete"
                    className="text-[var(--text-danger)]"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <FileText />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No processes found
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {search || filterCategory || filterStatus !== "all"
                ? "Try adjusting your filters."
                : "Create your first process template to get started."}
            </p>
            {!search && !filterCategory && filterStatus === "all" && (
              <Button className="mt-4" onClick={onNew}>
                <Plus className="h-4 w-4" />
                New Process
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Preview modal (fetches full template with steps) */}
      {previewId && previewTemplate && (
        <ProcessPreview
          name={previewTemplate.name}
          description={previewTemplate.description}
          category={previewTemplate.category}
          icon={previewTemplate.icon}
          estimatedMinutes={previewTemplate.estimated_minutes}
          scoringEnabled={previewTemplate.scoring_enabled}
          steps={(previewTemplate.steps || []).map((s) => ({
            step_type: s.step_type,
            label: s.label,
            description: s.description,
            is_required: s.is_required,
            section: s.section,
            config: s.config,
          }))}
          onClose={() => setPreviewId(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main: ProcessesPage
// ---------------------------------------------------------------------------

function ProcessesPage() {
  const { data: categories } = useCategories();
  const [view, setView] = useState<"library" | "builder">("library");
  const [editingTemplate, setEditingTemplate] =
    useState<ProcessTemplateSummary | null>(null);

  const openNew = () => {
    setEditingTemplate(null);
    setView("builder");
  };

  const openEdit = (tpl: ProcessTemplateSummary) => {
    setEditingTemplate(tpl);
    setView("builder");
  };

  const closeBuilder = () => {
    setEditingTemplate(null);
    setView("library");
  };

  if (view === "builder") {
    return (
      <ProcessBuilder
        key={editingTemplate?.id ?? "new"}
        template={editingTemplate}
        categories={categories ?? []}
        onClose={closeBuilder}
      />
    );
  }

  return <ProcessLibrary onNew={openNew} onEdit={openEdit} />;
}

export default ProcessesPage;
